import { QUALITY_PRESETS, type QualityPreset } from "./format";

export interface ExtractedVideo {
  pack: {
    width: number;
    height: number;
    fps: number;
    durationSec: number;
    frameCount: number;
    rgba: ArrayBuffer;
  };
  objectUrl: string;
}

function wait(video: HTMLVideoElement, event: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const onErr = () => reject(new Error("Video failed to load"));
    video.addEventListener(event, () => resolve(), { once: true });
    video.addEventListener("error", onErr, { once: true });
  });
}

export async function extractFrames(
  source: File | Blob,
  quality: QualityPreset,
  onProgress?: (ratio: number, phase: string) => void,
): Promise<ExtractedVideo> {
  const preset = QUALITY_PRESETS[quality];
  const objectUrl = URL.createObjectURL(source);
  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.preload = "auto";
  video.src = objectUrl;

  await wait(video, "loadedmetadata");
  if (!Number.isFinite(video.duration) || video.duration <= 0) {
    throw new Error("Could not read video duration");
  }

  const durationSec = Math.min(video.duration, 8);
  const nativeWidth = video.videoWidth || 640;
  const nativeHeight = video.videoHeight || 360;
  const scale = Math.min(1, preset.maxWidth / nativeWidth);
  const width = Math.max(16, Math.round((nativeWidth * scale) / 2) * 2);
  const height = Math.max(16, Math.round((nativeHeight * scale) / 2) * 2);

  const step = 1 / preset.targetFps;
  const times: number[] = [];
  for (let t = 0; t < durationSec - 1e-3 && times.length < preset.maxFrames; t += step) {
    times.push(Math.min(t, durationSec - 1e-3));
  }
  if (times.length === 0) times.push(0);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new Error("2D canvas unavailable");

  const stride = width * height * 4;
  const rgba = new Uint8Array(stride * times.length);

  for (let i = 0; i < times.length; i++) {
    video.currentTime = times[i];
    await wait(video, "seeked");
    ctx.drawImage(video, 0, 0, width, height);
    const img = ctx.getImageData(0, 0, width, height);
    rgba.set(img.data, i * stride);
    onProgress?.((i + 1) / times.length, "Extracting frames");
  }

  video.removeAttribute("src");
  video.load();

  const fps = times.length > 1 ? (times.length - 1) / (times[times.length - 1] - times[0] || 1) : preset.targetFps;

  return {
    objectUrl,
    pack: {
      width,
      height,
      fps,
      durationSec,
      frameCount: times.length,
      rgba: rgba.buffer,
    },
  };
}
